// Group LogEntry[] by the date portion of each entry's `ts` field.
//
// ts format (from the live API): "YYYY-MM-DD HH:MM:SS" — the existing
// terminalLogRow() in ./log.ts already splits this on the first space
// and treats [0] as the date and [1] as the time. We use the same
// extraction so date buckets are consistent with the per-row rendering.
//
// Mirrors the shape of groupTerminalsByOid() in ../render.ts (Bucket[]
// with key + name + items), so the call-site pattern is familiar.
// Used by renderGlobalLogs (Activity Logs page) and renderTerminalLogs
// (per-terminal modal) to render collapsible date headers.
//
// Pure function — no DOM, no module state. Reads LogEntry shape from
// ../types. The bucket's logs[] preserves the input order; the caller
// is responsible for sorting the input array (typically reverse-chronological,
// so newest log lands in the first bucket).

import type { LogEntry } from '../types';

export interface DateBucket {
  /** The date string ("YYYY-MM-DD") the bucket represents, or
   *  "__unknown__" if any entry in the bucket had a missing/malformed ts. */
  date: string;
  /** Logs belonging to this date, in the order they appeared in the input. */
  logs: LogEntry[];
}

/** Marker for entries whose ts is missing or doesn't contain a space
 *  (i.e. no date portion extractable). Renderers can decide whether
 *  to surface this bucket or fold it into the most recent bucket. */
export const UNKNOWN_DATE = '__unknown__';

/** Extract the date portion of a LogEntry's ts. Returns UNKNOWN_DATE
 *  if ts is missing or has no space (no date separator). */
export function extractLogDate(ts: string | null | undefined): string {
  if (typeof ts !== 'string' || ts.length === 0) return UNKNOWN_DATE;
  const idx = ts.indexOf(' ');
  if (idx <= 0) return UNKNOWN_DATE;
  return ts.substring(0, idx);
}

/** Group logs into date buckets. The output is ordered DESCENDING by
 *  date string (newest first). Within each bucket, the order matches
 *  the input — no per-bucket sort. The UNKNOWN_DATE bucket (if any)
 *  is placed LAST regardless of lexicographic order, since it's not
 *  a real date and shouldn't compete with real ones for the "top" slot.
 *
 *  Empty input returns an empty array.
 */
export function groupLogsByDate(logs: LogEntry[] | null | undefined): DateBucket[] {
  if (!Array.isArray(logs) || logs.length === 0) return [];

  // First pass: bucket the entries, preserving input order within each bucket.
  const byDate = new Map<string, LogEntry[]>();
  for (const l of logs) {
    const date = extractLogDate(l?.ts);
    if (!byDate.has(date)) byDate.set(date, []);
    byDate.get(date)!.push(l);
  }

  // Second pass: order. Real dates DESCENDING (newest first). Unknown last.
  const real: DateBucket[] = [];
  let unknown: DateBucket | null = null;
  for (const [date, entries] of byDate) {
    if (date === UNKNOWN_DATE) {
      unknown = { date, logs: entries };
    } else {
      real.push({ date, logs: entries });
    }
  }
  real.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

  return unknown ? [...real, unknown] : real;
}

// =====================================================================
// HTML helpers for the date-grouped log views.
//
// `dateHeaderHtml()` returns the HTML for a single collapsible date
// header — a <details> element wrapping the bucket's logs. The native
// <details>/<summary> gives us collapsible behavior for free, with no
// extra JS click handlers. `openByDefault` controls whether the group
// starts expanded.
//
// The current date is passed in by the caller (from the browser clock
// or the server clock via state.globalData.drift_srv2local). Doing the
// "today vs older" comparison inside the helper would require reading
// the clock here, which would couple the helper to module-scope state
// — instead the caller decides.
//
// `renderBucketHtml()` renders a single bucket: date header + the
// logs' rows (using the per-row renderer passed in by the caller, so
// the global-log and per-terminal-log views can use their own row
// shapes).
// =====================================================================

import { escapeHtml } from './html';

/** Returns HTML for a collapsible date header wrapping the bucket's
 *  log rows. `rowHtml` is a function that takes a LogEntry and returns
 *  its HTML — injected by the caller so per-terminal vs per-global
 *  can use their own row shapes.
 *  - `date`: the bucket's date string (or "__unknown__")
 *  - `logs`: the bucket's entries
 *  - `today`: the current date string ("YYYY-MM-DD") in the chosen
 *    timezone — used to decide whether to default-open the group
 *  - `rowHtml`: per-entry HTML renderer
 *  - `openByDefault`: when true, every bucket starts expanded
 *    regardless of `today`. Used by the dashboard notify panel
 *    (renderNotify in admin/src/render.ts) where the small
 *    ~50-row feed is the user's primary action surface and
 *    forcing the user to click each date header to see what
 *    happened hides the very rows they came for. Activity Logs
 *    and per-terminal logs keep the legacy "today open, others
 *    collapsed" behaviour by leaving this flag false.
 */
export function renderBucketHtml(
  bucket: DateBucket,
  today: string,
  rowHtml: (l: LogEntry) => string,
  openByDefault: boolean = false,
): string {
  // Render the per-row HTML once, outside the details wrapper for
  // easier incremental appends (each row lives as a direct child of
  // its date group, so we can `insertAdjacentHTML('beforeend', row)`
  // to append a new entry to its bucket without re-rendering anything).
  const isToday = bucket.date === today;
  const open = openByDefault || isToday;
  const rowsHtml = bucket.logs.map(rowHtml).join('');
  const labelText = isToday
    ? `${escapeHtml(bucket.date)} - Today`
    : (bucket.date === UNKNOWN_DATE
        ? 'Unknown date'
        : escapeHtml(bucket.date));
  const count = bucket.logs.length;
  return `<details class="date-group" data-date="${escapeHtml(bucket.date)}"${open ? ' open' : ''}>
    <summary class="cursor-pointer text-sm font-semibold text-gray-700 hover:text-gray-900 py-2 px-3 bg-gray-50 mb-2 flex justify-between items-center">
      <span>${labelText}</span>
      <span class="text-xs font-normal text-gray-500">${count} entr${count === 1 ? 'y' : 'ies'}</span>
    </summary>
    <div class="bucket-rows space-y-1 md:pl-3 pb-3 mb-3">
      ${rowsHtml}
    </div>
  </details>`;
}

/** Notify-panel-specific bucket renderer. Same shape as
 *  renderBucketHtml() but with two behaviour differences baked in:
 *  every bucket defaults open (the notify feed is small and the
 *  user's primary action surface), and the `today` argument is
 *  dropped because we no longer differentiate the "today" label —
 *  every bucket shows its raw date in the header.
 *
 *  Kept as a separate helper rather than renderBucketHtml(...,
 *  openByDefault=true) so the notify panel's bucket shape and the
 *  audit-log bucket shape can evolve independently. If a future
 *  phase wants the notify bucket to render a "Mark all read"
 *  button, an "x" dismiss on the header, or a per-bucket unread
 *  count, that change lands here without touching the audit-log
 *  code path.
 *
 *  Same input contract as renderBucketHtml:
 *  - `bucket`: the DateBucket to render
 *  - `rowHtml`: per-entry HTML renderer (e.g. terminalNotificationLogRow)
 */
export function renderNotifyBucket(
  bucket: DateBucket,
  rowHtml: (l: LogEntry) => string,
): string {
  const rowsHtml = bucket.logs.map(rowHtml).join('');
  const count = bucket.logs.length;
  const labelText = bucket.date === UNKNOWN_DATE
    ? 'Unknown date'
    : escapeHtml(bucket.date);
  return `<details class="date-group" data-date="${escapeHtml(bucket.date)}" open>
    <summary class="cursor-pointer text-sm font-semibold text-gray-700 hover:text-gray-900 py-2 px-3 bg-gray-50 mb-2 flex justify-between items-center">
      <span>${labelText}</span>
      <span class="text-xs font-normal text-gray-500">${count} entr${count === 1 ? 'y' : 'ies'}</span>
    </summary>
    <div class="bucket-rows gap-x-1.5 space-y-1 p-3 pb-3 mb-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 ">
      ${rowsHtml}
    </div>
  </details>`;
}

/** Format a Date as "YYYY-MM-DD" in the local timezone. Used to compute
 *  the "today" string that decides which date group defaults open.
 *  Caller passes in a Date (default: new Date()), and the helper does
 *  the format. Kept here rather than in time.ts so the date-grouping
 *  module is self-contained.
 */
export function formatLocalDate(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
